import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SegOption {
    label: string;
    value: any;
    color?: 'green' | 'amber' | 'red' | 'gray';
}

/**
 * Selettore a pulsanti segmentati colorati: un tap per scegliere, valore sempre
 * visibile, codifica colore (verde→rosso). Pensato per l'operatore in campo al
 * posto dei menu a tendina. Funziona con formControlName (ControlValueAccessor).
 *
 * palette:
 *  - 'state3'  : 3 stati → verde / ambra / rosso (es. Buono/Sufficiente/Scarso)
 *  - 'percent' : valori numerici/percentuali → verde(≥75)/ambra(≥30)/rosso
 *  - 'none'    : neutro (grigio quando selezionato)
 */
@Component({
    selector: 'app-segmented-select',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="seg" [class.seg-wrap]="wrap">
            <button
                type="button"
                *ngFor="let o of normalized"
                class="seg-btn"
                [class.selected]="o.value === value"
                [attr.data-color]="o.color"
                [disabled]="disabled"
                (click)="select(o.value)"
            >
                {{ o.label }}
            </button>
        </div>
    `,
    styles: [
        `
            .seg {
                display: inline-flex;
                flex-wrap: nowrap;
                border-radius: 0.6rem;
                overflow: hidden;
                border: 1px solid var(--surface-border);
                max-width: 100%;
            }
            .seg-wrap {
                flex-wrap: wrap;
                gap: 0.4rem;
                border: none;
            }
            .seg-btn {
                flex: 1 1 auto;
                padding: 0.5rem 0.8rem;
                font-weight: 400;
                font-size: 0.95rem;
                line-height: 1.1;
                background: transparent;
                color: var(--text-color-secondary);
                border: none;
                border-right: 1px solid var(--surface-border);
                cursor: pointer;
                white-space: nowrap;
                transition:
                    background 0.12s ease,
                    color 0.12s ease;
            }
            .seg-btn:last-child {
                border-right: none;
            }
            .seg-btn:hover:not(.selected):not(:disabled) {
                background: color-mix(in srgb, var(--primary-color) 8%, var(--surface-card));
            }
            .seg-btn:disabled {
                opacity: 0.55;
                cursor: default;
            }

            /* Chip separati quando va a capo (molti valori) */
            .seg-wrap .seg-btn {
                flex: 0 0 auto;
                border: 1px solid var(--surface-border);
                border-radius: 0.5rem;
                min-width: 3.25rem;
                text-align: center;
            }

            /* Stato selezionato per colore */
            .seg-btn.selected {
                color: #fff;
                font-weight: 600;
            }
            .seg-btn.selected[data-color='green'] {
                background: #16a34a;
                border-color: #16a34a;
            }
            .seg-btn.selected[data-color='amber'] {
                background: #d97706;
                border-color: #d97706;
            }
            .seg-btn.selected[data-color='red'] {
                background: #dc2626;
                border-color: #dc2626;
            }
            .seg-btn.selected[data-color='gray'] {
                background: #475569;
                border-color: #475569;
            }
        `
    ],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SegmentedSelectComponent), multi: true }]
})
export class SegmentedSelectComponent implements ControlValueAccessor {
    @Input() options: (string | SegOption)[] = [];
    /** true = chip separati che vanno a capo (per molti valori, es. percentuali) */
    @Input() wrap = false;
    @Input() palette: 'state3' | 'percent' | 'none' = 'none';

    value: any = null;
    disabled = false;

    private onChange: (v: any) => void = () => {};
    private onTouched: () => void = () => {};

    get normalized(): SegOption[] {
        const src = this.options ?? [];
        let list = src.map((o, i) => {
            const opt: SegOption = typeof o === 'string' ? { label: o, value: o } : { ...o };
            if (!opt.color) opt.color = this.colorFor(opt.value, i, src.length);
            return opt;
        });
        // Percentuali: sempre crescenti, da 0 a 100 (sinistra → destra)
        if (this.palette === 'percent') {
            list = list.sort((a, b) => Number(a.value) - Number(b.value));
        }
        return list;
    }

    private colorFor(value: any, i: number, n: number): SegOption['color'] {
        if (this.palette === 'state3') {
            if (i === 0) return 'green';
            if (i === n - 1) return 'red';
            return 'amber';
        }
        if (this.palette === 'percent') {
            const num = Number(value);
            if (isNaN(num)) return 'gray';
            if (num >= 75) return 'green';
            if (num >= 30) return 'amber';
            return 'red';
        }
        return 'gray';
    }

    select(v: any): void {
        if (this.disabled) return;
        // Riclic sullo stesso valore = deseleziona
        this.value = this.value === v ? null : v;
        this.onChange(this.value);
        this.onTouched();
    }

    writeValue(v: any): void {
        this.value = v;
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState(d: boolean): void {
        this.disabled = d;
    }
}
